// FailoverOptionsModalClassic.jsx
import React, { useMemo, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./failOverModal.css";
import { updateFailoverOptions } from "../../../../../actions/EpikBoxes";
import { connect, useDispatch } from "react-redux";
import InfoPopup from "../../../../InfoPopup";
import { TOOLTIPS_TEXT } from "../../../../../lib/constants";

const LIMITS = {
  DEVICE_MAX_LATENCY: { key: "Device Max Latency", min: 100, max: 900, def: 900 },
  PING_COUNT: { key: "Ping Count", min: 10, max: 100, def: 10 },
  MISSED_PKT: { key: "Missed Packet Value", min: 1000, max: 2000, def: 1000 },
  IFACE_MAX_AVG: { key: "Max Average Latency", min: 100, max: 900, def: 500 },
  IFACE_MIN_IMPROVE: { key: "Minimum Improvement to Restore", min: 20, max: 500, def: 50 }
};

const inRange = (v, { min, max }) => v >= min && v <= max;
const prettyRange = ({ min, max }) => `${min}–${max}`;

const pickValue = (editVal, deviceVal, def) => {
  if (editVal !== "" && editVal !== null && editVal !== undefined) {
    const n = Number(editVal);
    if (Number.isFinite(n)) return n;
  }
  if (deviceVal !== "" && deviceVal !== null && deviceVal !== undefined) {
    const n2 = Number(deviceVal);
    if (Number.isFinite(n2)) return n2;
  }
  return def;
};

const pickIfaceValue = (editMap, iface, deviceByName, def) => {
  const edit = editMap[iface];
  if (edit !== "" && edit !== null && edit !== undefined) {
    const n = Number(edit);
    if (Number.isFinite(n)) return n;
  }
  const dev = deviceByName[iface];
  if (dev !== "" && dev !== null && dev !== undefined) {
    const n2 = Number(dev);
    if (Number.isFinite(n2)) return n2;
  }
  return def;
};
const DeviceRow = ({
  label,
  tooltip,
  tooltipId,
  min,
  max,
  settingValue,
  onSetting,
  deviceValue,
  invalid,
}) => {
  return (
    <div className="row-grid">
      <div className="cell cell--label">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {label}
          {tooltip && tooltipId && (
            <InfoPopup id={tooltipId} text={tooltip} placement="right" />
          )}
        </span>
      </div>
      <div className="cell cell--setting">
        <Input
          type="number"
          min={min}
          max={max}
          value={settingValue}
          onChange={e => onSetting(e.target.value)}
          invalid={!!invalid}
        />
      </div>
      <div className="cell cell--device">
        <Input value={deviceValue} disabled readOnly/>
      </div>
    </div>
  );
};
const IfaceRow = ({
  name,
  min,
  max,
  settingValue,
  onSetting,
  deviceValue,
  invalid,
  tooltip,      
  tooltipId,  
}) => {
  const tipId = tooltipId || `iface-tip-${name}}`;
  return (
    <div className="row-grid tight">
      <div className="cell cell--label">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {name}
          {tooltip && <InfoPopup id={tipId} text={tooltip} placement="right" />}
        </span>
      </div>
      <div className="cell cell--setting">
        <Input
          type="number"
          min={min}
          max={max}
          value={settingValue}
          onChange={e => onSetting(e.target.value)}
          invalid={!!invalid}
        />
      </div>
      <div className="cell cell--device">
        <Input value={deviceValue} disabled readOnly />
      </div>
    </div>
  );
};

function FailoverOptionsModalClassic({
  isOpen,
  onClose,
  onRefresh,
  loadingMeta = false,
  failOverOptionsData = {},
  boxDetails
}) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("jwtToken") || null;

  const ifaceList = useMemo(() => {
    const toArr = v =>
      Array.isArray(v)
        ? v
        : String(v || "")
            .replace(/[”"]/g, '"')
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
    // const fromBox = toArr(failOverOptionsData?.box?.failOverOptions?.deviceSettings?.interfaces);
    const fromApi = toArr(failOverOptionsData?.failOverOptionsApi?.interfaces);
    const base = fromApi.length ? fromApi : []
    const out = [];
    const pushUnique = arr => arr.forEach(x => { if (x && !out.includes(x)) out.push(x); });
    pushUnique(base);
    // pushUnique(fromBox);
    // pushUnique(fromApi);
    return out;
  }, [
    failOverOptionsData?.box?.failOverOptions?.deviceSettings?.interfaces,
    failOverOptionsData?.failOverOptionsApi?.interfaces
  ]);

  const [maxLatency, setMaxLatency] = useState("");
  const [pingCount, setPingCount] = useState("");
  const [missedPacketValue, setMissedPacketValue] = useState("");

  const [deviceVals, setDeviceVals] = useState({
    maxLatency: "",
    pingCount: "",
    missedPacketValue: ""
  });

  const [ifaceMaxAvgLatency, setIfaceMaxAvgLatency] = useState({});
  const [ifaceMinImproveRestore, setIfaceMinImproveRestore] = useState({});

  const apiIfaceOrder = useMemo(() => {
    const s = failOverOptionsData?.failOverOptionsApi?.interfaces || "";
    return String(s)
      .replace(/[”"]/g, '"')
      .split(",")
      .map(t => t.trim())
      .filter(Boolean);
  }, [failOverOptionsData?.failOverOptionsApi?.interfaces]);

  const ifaceDeviceMaxByName = useMemo(() => {
    const vals = String(failOverOptionsData?.failOverOptionsApi?.latencyTrigger || "")
      .replace(/[”]/g, "")
      .split(",")
      .map(v => v.trim());
    const map = {};
    apiIfaceOrder.forEach((iface, i) => { map[iface] = vals[i] ?? ""; });
    return map;
  }, [failOverOptionsData?.failOverOptionsApi?.latencyTrigger, apiIfaceOrder]);

  const ifaceDeviceMinByName = useMemo(() => {
    const vals = String(failOverOptionsData?.failOverOptionsApi?.HYSTERESIS || "")
      .split(",")
      .map(v => v.trim());
    const map = {};
    apiIfaceOrder.forEach((iface, i) => { map[iface] = vals[i] ?? ""; });
    return map;
  }, [failOverOptionsData?.failOverOptionsApi?.HYSTERESIS, apiIfaceOrder]);
  /* ----------------------------------------------------------------------------------- */

  const [deviceErr, setDeviceErr] = useState({ maxLatency: false, pingCount: false, missedPacketValue: false });
  const [ifaceErrMax, setIfaceErrMax] = useState({});
  const [ifaceErrMin, setIfaceErrMin] = useState({});

  // useEffect(() => {
  //   setIfaceMaxAvgLatency(prev => {
  //     const next = {};
  //     ifaceList.forEach(i => (next[i] = prev[i] ?? ""));
  //     return next;
  //   });
  //   setIfaceMinImproveRestore(prev => {
  //     const next = {};
  //     ifaceList.forEach(i => (next[i] = prev[i] ?? ""));
  //     return next;
  //   });
  //   setIfaceErrMax(Object.fromEntries(ifaceList.map(i => [i, false])));
  //   setIfaceErrMin(Object.fromEntries(ifaceList.map(i => [i, false])));
  // }, [ifaceList]);

  useEffect(() => {
    if (!isOpen || loadingMeta) return;
    const savedFo = failOverOptionsData?.box?.failOverOptions || {};
    const savedDS = savedFo.deviceSettings || savedFo.deviceSetting || {};
    const savedIface = savedFo.interfaceOption || savedFo.interfaceOptions || {};
    const mlSaved = savedDS.secureConnectionLatency ?? LIMITS.DEVICE_MAX_LATENCY.def;
    const pcSaved = savedDS.pingCount ?? LIMITS.PING_COUNT.def;
    const mpSaved = savedDS.missedPktValue ?? LIMITS.MISSED_PKT.def;
    // setMaxLatency(String(mlSaved));
    // setPingCount(String(pcSaved));
    // setMissedPacketValue(String(mpSaved));
    const api = failOverOptionsData?.failOverOptionsApi || {};
    const mlApi = api.wireguardLatency ?? "";
    const pcApi = api.BUFFER_LENGTH ?? "";
    const mpApi = api.MISSED_PKT_VALUE ?? "";
    setDeviceVals({ maxLatency: String(mlApi), pingCount: String(pcApi), missedPacketValue: String(mpApi) });
    // setIfaceMaxAvgLatency(prev => {
    //   const next = { ...prev };
    //   ifaceList.forEach(name => {
    //     const v = savedIface?.maxAverageLatency?.[name];
    //     next[name] = v !== undefined ? String(v) : (next[name] || String(LIMITS.IFACE_MAX_AVG.def));
    //   });
    //   return next;
    // });
    // setIfaceMinImproveRestore(prev => {
    //   const next = { ...prev };
    //   ifaceList.forEach(name => {
    //     const v = savedIface?.minimumImprovement?.[name];
    //     next[name] = v !== undefined ? String(v) : (next[name] || String(LIMITS.IFACE_MIN_IMPROVE.def));
    //   });
    //   return next;
    // });
    setDeviceErr({ maxLatency: false, pingCount: false, missedPacketValue: false });
    setIfaceErrMax(Object.fromEntries(ifaceList.map(i => [i, false])));
    setIfaceErrMin(Object.fromEntries(ifaceList.map(i => [i, false])));
  }, [isOpen, loadingMeta, failOverOptionsData, ifaceList]);

  const handleRefresh = () => {
    onRefresh?.();
    setMaxLatency("");
    setPingCount("");
    setMissedPacketValue("");
    setIfaceMaxAvgLatency('')
    setIfaceMinImproveRestore('')
  };

   const handleClose = () => {
    setMaxLatency("");
    setPingCount("");
    setMissedPacketValue("");
    setIfaceMaxAvgLatency("");      
    setIfaceMinImproveRestore("");  
    setDeviceErr({ maxLatency: false, pingCount: false, missedPacketValue: false });
    setIfaceErrMax({});
    setIfaceErrMin({});
    onClose?.();
  };
  const validateAndBuild = () => {
    let ok = true;
    const errors = [];
    const dErr = { maxLatency: false, pingCount: false, missedPacketValue: false };
    const iErrMax = {};
    const iErrMin = {};

    const outDevice = {
      maxLatency: pickValue(maxLatency, deviceVals.maxLatency, LIMITS.DEVICE_MAX_LATENCY.def),
      pingCount: pickValue(pingCount, deviceVals.pingCount, LIMITS.PING_COUNT.def),
      missedPacketValue: pickValue(missedPacketValue, deviceVals.missedPacketValue, LIMITS.MISSED_PKT.def),
      interfaces: ifaceList.join(",")
    };

    if (!inRange(outDevice.maxLatency, LIMITS.DEVICE_MAX_LATENCY)) { dErr.maxLatency = true; ok = false; errors.push(`${LIMITS.DEVICE_MAX_LATENCY.key}: ${prettyRange(LIMITS.DEVICE_MAX_LATENCY)}`); }
    if (!inRange(outDevice.pingCount, LIMITS.PING_COUNT)) { dErr.pingCount = true; ok = false; errors.push(`${LIMITS.PING_COUNT.key}: ${prettyRange(LIMITS.PING_COUNT)}`); }
    if (!inRange(outDevice.missedPacketValue, LIMITS.MISSED_PKT)) { dErr.missedPacketValue = true; ok = false; errors.push(`${LIMITS.MISSED_PKT.key}: ${prettyRange(LIMITS.MISSED_PKT)}`); }
    const outIfaceMax = {};
    const outIfaceMin = {};
    ifaceList.forEach(name => {
      const vMax = pickIfaceValue(ifaceMaxAvgLatency, name, ifaceDeviceMaxByName, LIMITS.IFACE_MAX_AVG.def);
      const vMin = pickIfaceValue(ifaceMinImproveRestore, name, ifaceDeviceMinByName, LIMITS.IFACE_MIN_IMPROVE.def);
      outIfaceMax[name] = vMax;
      outIfaceMin[name] = vMin;
      iErrMax[name] = !inRange(vMax, LIMITS.IFACE_MAX_AVG);
      iErrMin[name] = !inRange(vMin, LIMITS.IFACE_MIN_IMPROVE);
      if (iErrMax[name]) errors.push(`${name}: ${LIMITS.IFACE_MAX_AVG.key} ${prettyRange(LIMITS.IFACE_MAX_AVG)}`);
      if (iErrMin[name]) errors.push(`${name}: ${LIMITS.IFACE_MIN_IMPROVE.key} ${prettyRange(LIMITS.IFACE_MIN_IMPROVE)}`);
      if (iErrMax[name] || iErrMin[name]) ok = false;
    });

    setDeviceErr(dErr);
    setIfaceErrMax(iErrMax);
    setIfaceErrMin(iErrMin);

    if (!ok) {
      toast.error(
        <div>
          <strong>Please fix these fields:</strong>
          <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
            {errors.map((e, i) => (<li key={i} style={{ marginBottom: 4 }}>{e}</li>))}
          </ul>
        </div>,
        { autoClose: 4000, containerId: "failover-toasts" }
      );
      return { ok: false };
    }

    return { ok: true, outDevice, outIfaceMax, outIfaceMin };
  };

  const handleApply = async () => {
    const res = validateAndBuild();
    if (!res.ok) return;
    const payload = {
      deviceSettings: {
        maxLatency: res.outDevice.maxLatency,
        pingCount: res.outDevice.pingCount,
        missedPacketValue: res.outDevice.missedPacketValue,
        interfaces: res.outDevice.interfaces
      },
      interfaceOptions: {
        maxAverageLatency: res.outIfaceMax,
        minimumImprovementToRestore: res.outIfaceMin,
        interfaces: res.outDevice.interfaces
      }
    };
    try {
      setSaving(true);
      const data  = await dispatch(updateFailoverOptions(token, boxDetails._id, payload));
        onRefresh?.();
       await handleClose()
      setIfaceMaxAvgLatency('')
      setIfaceMinImproveRestore('')
    } catch {
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="failover-modal">
      <ModalHeader toggle={handleClose}><b>Failover Options</b></ModalHeader>
      <ModalBody>
        <div className="section">
          <div className="section-title underline">Device Settings</div>
          <div className="grid-headers">
            <div />
            <div className="grid-header">Setting</div>
            <div className="grid-header">Device</div>
          </div>
          <DeviceRow
            label={"Secure Connection Max Latency"}
            min={LIMITS.DEVICE_MAX_LATENCY.min}
            tooltip={TOOLTIPS_TEXT.SECURE_CONNECTION}
            tooltipId="secure"
            max={LIMITS.DEVICE_MAX_LATENCY.max}
            settingValue={maxLatency}
            onSetting={setMaxLatency}
            deviceValue={deviceVals.maxLatency}
            invalid={deviceErr.maxLatency}
          />
          <DeviceRow
            label={"Ping Count"}
            min={LIMITS.PING_COUNT.min}
            tooltip={TOOLTIPS_TEXT.PING_COUNT}
            tooltipId="pingcount"
            max={LIMITS.PING_COUNT.max}
            settingValue={pingCount}
            onSetting={setPingCount}
            deviceValue={deviceVals.pingCount}
            invalid={deviceErr.pingCount}
          />
          <DeviceRow
            label={"Missed Packet Value"}
            tooltip={TOOLTIPS_TEXT.MISSED_PKT_VALUE}
            tooltipId="pktvalue"
            min={LIMITS.MISSED_PKT.min}
            max={LIMITS.MISSED_PKT.max}
            settingValue={missedPacketValue}
            onSetting={setMissedPacketValue}
            deviceValue={deviceVals.missedPacketValue}
            invalid={deviceErr.missedPacketValue}
          />
        </div>

        <div className="h-sep" />

        <div className="section">
          <div className="section-title underline">Interface Options</div>
          <div className="iface-wrap">
            <div className="iface-panel">
              <div className="iface-title">Max Average Latency</div>
              <div className="grid-headers">
                <div />
                <div className="grid-header">Setting</div>
                <div className="grid-header">Device</div>
              </div>
              {ifaceList.map(name => (
                <IfaceRow
                  key={`max-${name}`}
                  name={name}
                  tooltip={TOOLTIPS_TEXT.MAX_AVERAGE_LATENCY}
                  tooltipId={`max-${name}`}
                  min={LIMITS.IFACE_MAX_AVG.min}
                  max={LIMITS.IFACE_MAX_AVG.max}
                  settingValue={ifaceMaxAvgLatency[name] ?? ""}
                  onSetting={v => setIfaceMaxAvgLatency(s => ({ ...s, [name]: v }))}
                  deviceValue={ifaceDeviceMaxByName[name] ?? ""}
                  invalid={!!ifaceErrMax[name]}
                />
              ))}
            </div>

            <div className="v-sep" />
            <div className="iface-panel">
              <div className="iface-title">Minimum Improvement to Restore</div>
              <div className="grid-headers">
                <div />
                <div className="grid-header">Setting</div>
                <div className="grid-header">Device</div>
              </div>
              {ifaceList.map(name => (
                <IfaceRow
                  key={`min-${name}`}
                  name={name}
                 tooltip={TOOLTIPS_TEXT.MINIMUM_IMPROVEMENT}
                  tooltipId={`min-${name}`}
                  min={LIMITS.IFACE_MIN_IMPROVE.min}
                  max={LIMITS.IFACE_MIN_IMPROVE.max}
                  settingValue={ifaceMinImproveRestore[name] ?? ""}
                  onSetting={v => setIfaceMinImproveRestore(s => ({ ...s, [name]: v }))}
                  deviceValue={ifaceDeviceMinByName[name] ?? ""}
                  invalid={!!ifaceErrMin[name]}
                />
              ))}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="footer-3">
        <Button className="btn-purple" onClick={handleRefresh} disabled={saving}>Refresh</Button>
        <Button className="btn-wide btn-outline" onClick={handleClose} disabled={saving}>Close</Button>
        <Button className="btn-purple" onClick={handleApply} disabled={saving}>{saving ? "Saving..." : "Apply"}</Button>
      </ModalFooter>
    </Modal>
  );
}
const mapStateToProps = () => ({});
const mapDispatchToProps = () => ({});
export default connect(mapStateToProps, mapDispatchToProps)(FailoverOptionsModalClassic);
